import { useState } from 'react';
import { siteApi } from '../tjenester/siteApi';

const emptyInquiry = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

function InquiryForm({ propertyName = '', propertySlug = '' }) {
  const [form, setForm] = useState(emptyInquiry);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = 'Skriv inn navn.';
    }

    if (!form.message.trim()) {
      nextErrors.message = 'Skriv en melding.';
    } else if (form.message.trim().length < 10) {
      nextErrors.message = 'Meldingen må være minst 10 tegn.';
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = 'Skriv en gyldig e-postadresse.';
    }

    if (form.phone.trim() && !/^[0-9+\s()-]{8,}$/.test(form.phone.trim())) {
      nextErrors.phone = 'Skriv et gyldig telefonnummer.';
    }

    return nextErrors;
  };

  const updateField = (fieldName, value) => {
    setForm((current) => ({ ...current, [fieldName]: value }));
    setFieldErrors((current) => {
      if (!current[fieldName]) {
        return current;
      }

      const next = { ...current };
      delete next[fieldName];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    const nextErrors = validateForm();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setErrorMessage('Rett opp feltene som er markert før du sender.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await siteApi.submitInquiry({
        ...form,
        propertyName,
        propertySlug,
      });
      setSuccessMessage(response.message || 'Takk! Vi har mottatt henvendelsen din.');
      setFieldErrors({});
      setForm(emptyInquiry);
    } catch (error) {
      setErrorMessage(error.message || 'Klarte ikke sende henvendelsen. Prov igjen senere.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="grid gap-4" onSubmit={handleSubmit}>
      {propertyName ? (
        <p className="text-sm text-[#7c6b52]">
          Henvendelse om <span className="font-semibold text-[#163127]">{propertyName}</span>
        </p>
      ) : null}
      <label className="grid gap-2">
        <span className="text-sm font-semibold text-[#355046]">Navn</span>
        <input
          autoComplete="name"
          aria-describedby={fieldErrors.name ? 'inquiry-error-name' : undefined}
          aria-invalid={Boolean(fieldErrors.name)}
          aria-required="true"
          className={`rounded-2xl border bg-white px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4c3b] focus-visible:ring-offset-2 ${
            fieldErrors.name ? 'border-[#a23b3b]' : 'border-[#1832281f]'
          }`}
          onChange={(event) => updateField('name', event.target.value)}
          required
          type="text"
          value={form.name}
        />
        {fieldErrors.name ? (
          <span className="text-sm font-semibold text-[#a23b3b]" id="inquiry-error-name">
            {fieldErrors.name}
          </span>
        ) : null}
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2">
          <span className="text-sm font-semibold text-[#355046]">E-post (valgfritt)</span>
          <input
            autoComplete="email"
            aria-describedby={fieldErrors.email ? 'inquiry-error-email' : undefined}
            aria-invalid={Boolean(fieldErrors.email)}
            className={`rounded-2xl border bg-white px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4c3b] focus-visible:ring-offset-2 ${
              fieldErrors.email ? 'border-[#a23b3b]' : 'border-[#1832281f]'
            }`}
            onChange={(event) => updateField('email', event.target.value)}
            type="email"
            value={form.email}
          />
          {fieldErrors.email ? (
            <span className="text-sm font-semibold text-[#a23b3b]" id="inquiry-error-email">
              {fieldErrors.email}
            </span>
          ) : null}
        </label>
        <label className="grid gap-2">
          <span className="text-sm font-semibold text-[#355046]">Telefon (valgfritt)</span>
          <input
            autoComplete="tel"
            aria-describedby={fieldErrors.phone ? 'inquiry-error-phone' : undefined}
            aria-invalid={Boolean(fieldErrors.phone)}
            className={`rounded-2xl border bg-white px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4c3b] focus-visible:ring-offset-2 ${
              fieldErrors.phone ? 'border-[#a23b3b]' : 'border-[#1832281f]'
            }`}
            inputMode="tel"
            onChange={(event) => updateField('phone', event.target.value)}
            type="tel"
            value={form.phone}
          />
          {fieldErrors.phone ? (
            <span className="text-sm font-semibold text-[#a23b3b]" id="inquiry-error-phone">
              {fieldErrors.phone}
            </span>
          ) : null}
        </label>
      </div>
      <label className="grid gap-2">
        <span className="text-sm font-semibold text-[#355046]">Melding</span>
        <textarea
          aria-describedby={fieldErrors.message ? 'inquiry-error-message' : undefined}
          aria-invalid={Boolean(fieldErrors.message)}
          aria-required="true"
          className={`min-h-[120px] rounded-2xl border bg-white px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4c3b] focus-visible:ring-offset-2 ${
            fieldErrors.message ? 'border-[#a23b3b]' : 'border-[#1832281f]'
          }`}
          onChange={(event) => updateField('message', event.target.value)}
          required
          value={form.message}
        />
        {fieldErrors.message ? (
          <span className="text-sm font-semibold text-[#a23b3b]" id="inquiry-error-message">
            {fieldErrors.message}
          </span>
        ) : null}
      </label>
      {successMessage ? (
        <p aria-live="polite" className="rounded-2xl bg-[rgba(29,76,59,0.08)] px-4 py-3 text-sm font-semibold text-[#1d4c3b]">
          {successMessage}
        </p>
      ) : null}
      {errorMessage ? (
        <p aria-live="assertive" className="rounded-2xl bg-[rgba(162,59,59,0.08)] px-4 py-3 text-sm font-semibold text-[#a23b3b]">
          {errorMessage}
        </p>
      ) : null}
      <button
        className="inline-flex min-h-12 w-fit items-center justify-center rounded-full bg-[#1d4c3b] px-6 font-semibold text-[#f9f5ee] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4c3b] focus-visible:ring-offset-2"
        disabled={isSubmitting}
        type="submit"
      >
        {isSubmitting ? 'Sender ...' : 'Send henvendelse'}
      </button>
    </form>
  );
}

export default InquiryForm;
